import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { trackEvent } from '../utils/analytics';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AgeDeclaration } from '../components/auth/age-declaration';
import { SocialSignIn } from '../components/auth/social-sign-in';
import i18n from '../i18n';

export default function SignUpPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  // The age declaration gates both ways in — the form and Google — so it sits
  // above both. See AgeDeclaration for why it exists.
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { signUp, signInWithProvider } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // A page that sent somebody here to sign up first — the guardian accept
  // page, say — stashes where to come back to, as sign-in does.
  const state = location.state as { from?: string; enterStage?: { slug: string; name: string } } | null;
  const redirectTo = state?.from ?? '/';
  // Somebody who tapped "enter" on a stage and answered "myself": the account
  // is only the first half of what they asked for. They go back to the page
  // they came from with the entry dialog open on that stage
  // (useResumeStageEntry) rather than being entered here in the background —
  // the dialog is where their gender, date of birth, category and turn are
  // asked, and nothing here could ask them.
  const enterStage = state?.enterStage ?? null;

  const goBack = () => {
    navigate(redirectTo, enterStage ? { state: { resumeEntry: enterStage.slug } } : undefined);
  };

  const clearError = () => { if (formError) setFormError(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !surname || !username || !password || !passwordConfirmation) return;
    if (!ageConfirmed) {
      setFormError(t('auth.ageConfirmationRequired'));
      return;
    }

    setFormError(null);

    if (password !== passwordConfirmation) {
      setFormError(t('auth.passwordsDontMatch'));
      return;
    }

    const usernameRegex = /^[a-z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setFormError(t('invitation.usernameFormatError'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await signUp({
        email,
        name,
        surname,
        username,
        password,
        password_confirmation: passwordConfirmation,
        language: i18n.language,
      });
      if (result.success) {
        trackEvent('sign_up');
        goBack();
      } else if (result.error) {
        setFormError(result.error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    // The button is inert until the box is ticked, but the credential arrives
    // from Google's own script, so refuse it here too.
    if (!ageConfirmed) {
      setFormError(t('auth.ageConfirmationRequired'));
      return;
    }
    setFormError(null);
    const result = await signInWithProvider('google', credential);
    if (result.success) {
      trackEvent('sign_up');
      goBack();
    } else if (result.error) {
      setFormError(result.error);
    }
  };

  return (
    <div className="flex justify-center items-center my-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.signUp')}</CardTitle>
          <CardDescription>
            {enterStage
              ? t('stageEntry.signUpToEnter', { stage: enterStage.name })
              : t('auth.signUpDescription')}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <AgeDeclaration
              checked={ageConfirmed}
              onCheckedChange={(checked) => { setAgeConfirmed(checked); clearError(); }}
            />
            {/* Google's button is an iframe we cannot disable, so make it
                unreachable until the declaration is given. */}
            <div
              data-testid="social-sign-in"
              aria-disabled={!ageConfirmed}
              className={ageConfirmed ? undefined : 'pointer-events-none opacity-50'}
            >
              <SocialSignIn onGoogleCredential={handleGoogleCredential} googleText="signup_with" />
            </div>
            {formError && (
              <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
                {formError}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('profile.name')}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('profile.namePlaceholder')}
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setName(e.target.value); clearError(); }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surname">{t('profile.surname')}</Label>
                <Input
                  id="surname"
                  type="text"
                  placeholder={t('profile.surnamePlaceholder')}
                  value={surname}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSurname(e.target.value); clearError(); }}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">{t('profile.username')}</Label>
              <Input
                id="username"
                type="text"
                placeholder={t('profile.usernamePlaceholder')}
                value={username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setUsername(e.target.value.toLowerCase()); clearError(); }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEmail(e.target.value); clearError(); }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPassword(e.target.value); clearError(); }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">{t('auth.confirmPassword')}</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPasswordConfirmation(e.target.value); clearError(); }}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading || !ageConfirmed}>
              {isLoading ? t('auth.signingUp') : t('auth.signUp')}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link to="/sign-in" className="text-primary hover:underline">
                {t('auth.signInNow')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
